import { CoreTooltip } from '@featuring-corp/components';
import { CoreTooltipProps } from '@featuring-corp/components/dist/core-tooltip/type';
import { commonContainer, ellipsis, ellipsisLine } from '@styles/vanilla-extract/utils';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import clsx from 'clsx';
import React, {
	ComponentPropsWithoutRef,
	createElement,
	ElementType,
	useEffect,
	useRef,
	useState,
} from 'react';

export type EllipsisTextProps<T extends ElementType> = {
	element?: T;
	line?: number;
} & Omit<ComponentPropsWithoutRef<T>, 'title'> &
	Pick<
		CoreTooltipProps,
		| 'placement'
		| 'autoAdjust'
		| 'eventType'
		| 'onClose'
		| 'disabled'
		| 'active'
		| 'handleActive'
		| 'zIndex'
	>;

const EllipsisText = <T extends ElementType = 'p'>({
	element = 'p' as T,
	line = 1,
	className,
	placement = 'top',
	autoAdjust = true,
	title,
	eventType = 'hover',
	onClose,
	disabled,
	active,
	handleActive,
	zIndex = 1000,
	...props
}: EllipsisTextProps<T>) => {
	const textRef = useRef<HTMLParagraphElement>(null);
	const [isOverflown, setIsOverFlown] = useState(false);

	useEffect(() => {
		if (!textRef.current) return;

		const { scrollWidth, offsetWidth, scrollHeight, offsetHeight } = textRef.current;
		setIsOverFlown(scrollHeight > offsetHeight || scrollWidth > offsetWidth);
	}, [textRef, props.children]);

	if (isOverflown)
		return (
			<CoreTooltip
				{...props}
				title={title}
				text={props.children as string}
				placement={placement}
				disabled={!isOverflown || disabled}
				autoAdjust={autoAdjust}
				zIndex={zIndex}
				eventType={eventType}
				onClose={onClose}
				active={active}
				handleActive={handleActive}
			>
				{createElement(
					element,
					{
						className: clsx(ellipsis, commonContainer, className),
						style: assignInlineVars({ [ellipsisLine]: `${line}` }),
						...props,
						ref: textRef,
					},
					props.children,
				)}
			</CoreTooltip>
		);
	else
		return (
			<>
				{createElement(
					element,
					{
						className: clsx(ellipsis, commonContainer, className),
						style: assignInlineVars({ [ellipsisLine]: `${line}` }),
						...props,
						ref: textRef,
					},
					props.children,
				)}
			</>
		);
};

export default EllipsisText;
